import { Phone, Mail, ListChecks } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useState } from "react";
import { Tables, Json } from "@/types/database.types";
import { LucideIconByName, IconMap } from "@/components/ui/lucide-icons";

// Define a proper type for the contact info structure
interface ContactInfo {
  phone?: string;
  email?: string;
  [key: string]: Json | undefined;
}

type ChatHeaderProps = {
  agent: Tables<"agents">;
  organization?: Tables<"organizations">;
  brandColor: string;
  isPublic?: boolean;
  conversationId?: string | null;
};

export const ChatHeader = ({
  agent,
  organization,
  brandColor,
  isPublic,
  conversationId
}: ChatHeaderProps) => {
  // Header text: always organization name
  const displayName = organization?.name || "Assistant";
  // Header image: organization logo only (no fallback to agent avatar)
  const avatarUrl = organization?.logo_url;
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Agent specific setting for phone number display
  // Show phone if requirePhoneNumber is true or undefined (default), hide if false.
  const agentContactInfo = agent?.contact_info as { requirePhoneNumber?: boolean } | undefined;
  const shouldShowPhoneNumber = agentContactInfo?.requirePhoneNumber !== false;

  const handleDebugClick = async () => {
    if (!conversationId || !agent?.id) {
      toast({
        title: "No conversation data",
        description: "No active conversation to debug",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Fetch form fields for the agent
      const { data: formFields, error: formFieldsError } = await supabase
        .from('form_fields')
        .select('*')
        .eq('agent_id', agent.id);

      if (formFieldsError) throw formFieldsError;

      // Fetch form values for the conversation
      const { data: formValues, error: formValuesError } = await supabase
        .from('conversation_field_values')
        .select('*, form_fields(*)')
        .eq('conversation_id', conversationId);

      if (formValuesError) throw formValuesError;

      console.log("Form fields debug data:", {
        agent_id: agent.id,
        conversation_id: conversationId,
        formFields,
        formValues
      });

      toast({
        title: "Form data logged",
        description: `Found ${formFields?.length || 0} fields and ${formValues?.length || 0} values`,
      });

    } catch (error) {
      console.error("Error fetching form field data:", error);
      toast({
        variant: "destructive",
        title: "Error fetching form data",
        description: "Check console for details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col ${isPublic ? '' : 'dark:bg-background'}`}>
      <div
        className={`text-white px-5 py-4 flex items-center justify-between`}
        style={{ backgroundColor: brandColor }}
      >
        <div className="flex items-center gap-4">
          {avatarUrl ? (
            typeof avatarUrl === 'string' && IconMap[avatarUrl] ? (
              // If avatar_url is a Lucide icon name
              <div className="h-10 w-10 rounded-full bg-white p-1 flex items-center justify-center">
                <LucideIconByName name={avatarUrl} size={20} className="text-gray-700" />
              </div>
            ) : (
              // If avatar_url is an image URL
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-10 w-10 object-contain rounded-full bg-white p-1"
              />
            )
          ) : null}
          <div>
            <h2 className="font-semibold text-sm text-white">{displayName}</h2>
          </div>
        </div>

        {/* Contact buttons */}
        <div className="flex items-center gap-2">
          {isPublic && organization?.contact_info && (
            <>
              {/* Cast the JSON to our ContactInfo type */}
              {shouldShowPhoneNumber && ((organization.contact_info as unknown) as ContactInfo)?.phone && (
                <a
                  href={`tel:${((organization.contact_info as unknown) as ContactInfo).phone}`}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                  aria-label="Call us"
                >
                  <Phone size={16} />
                </a>
              )}
            </>
          )}
          {conversationId && (
            <button
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Form fields progress"
              title="Debug form fields"
              onClick={handleDebugClick}
              disabled={isLoading}
            >
              <ListChecks size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
